/**
 * №16.1 Stage C — data hooks for the AUTHOR surfaces (template list, the author
 * canvas, the block picker, derive review). Mirrors use-workflow.ts exactly: the
 * same `mcpClient.toolsCall` transport, react-query keys, and a `read` helper;
 * every write funnels through `workflow_patch_template` (the ONE authoring door,
 * W23/D-AU3) so the canvas and the MCP author path stay provably equivalent.
 *
 * Payload shapes mirror src/engine/wf/authoring-projection.ts locally (the console
 * convention — do not import server types into the bundle).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { mcpClient } from "../../lib/mcp-client";

async function read<T>(name: string, args: Record<string, unknown> = {}): Promise<T> {
  const r = await mcpClient.toolsCall<T>(name, args);
  if (r.isError) throw new Error(r.content?.[0]?.text ?? `${name} failed`);
  return r.structuredContent as T;
}

// ---- shapes (mirror authoring-projection.ts + validate.ts) ----------------

export interface TemplateVersion { version: number; status: string; source: string; }
export interface TemplateListItem {
  code: string; kind: string; subjectType: string; description: string; source: string;
  autoStart: boolean; anchorTypeFilter: string | null; anchorTypeField: string | null;
  activeVersion: number; draftVersion: number; latestVersion: number;
  versions: TemplateVersion[]; segmentCount: number; stepCount: number;
  runningInstances: number; totalInstances: number;
}
export interface Finding { severity: "error" | "warning" | "info"; code: string; segmentNo?: number; stepNo?: number; message: string; }
export interface ValidationResult { templateCode: string; version: number; valid: boolean; counts: { error: number; warning: number; info: number }; findings: Finding[]; }

export interface ConditionLeaf { leafNo: number; source: string; field: string; op: string; value: string | null; valueType: string; }
export interface EffectView { effectNo: number; ownerKind: string; ownerNo: number; outcomeCode: string | null; event: string; sequence: number; verb: string; params: unknown; }
export interface StepView {
  stepNo: number; segmentNo: number; name: string; description: string; instructions: string;
  action: string; params: unknown; fallback: string; autoApproved: boolean;
  assigneeKind: string; assigneeCode: string | null; optional: boolean; required: boolean; displayOrder: number;
  dueTriggerKind: string | null; dueTriggerDateType: string | null; dueTriggerStepNo: number; dueOffsetDays: number;
  binding: { kind: string; eventKey: string; filters: unknown } | null;
  conditionLogic: string; conditions: ConditionLeaf[]; dependsOn: number[];
  manifest: { itemNo: number; description: string; direction: string; mandatory: boolean }[];
  planLines: { lineNo: number; resourceKind: string; code: string | null; quantity: number | null; rate: number | null }[];
  effects: EffectView[];
  block: { code: string; version: number; stepNo: number } | null;
}
export interface SegmentView {
  segmentNo: number; name: string; displayOrder: number;
  triggerKind: string; triggerDateType: string | null; triggerSegmentNo: number; triggerOutcomeCode: string | null;
  joinMode: string; hasDecision: boolean; decisionName: string | null; stepsOrdered: boolean;
  conditionLogic: string; conditions: ConditionLeaf[]; dependsOn: number[];
  outcomes: { code: string; label: string; isDefault: boolean }[];
  effects: EffectView[]; steps: StepView[];
}
export interface EndingView { segmentNo: number; outcomeCode: string; successorTemplateCode: string; anchorMode: string; anchorSubjectType: string | null; anchorSource: string | null; label: string; }
export interface TemplateDetail {
  found: boolean;
  template: {
    code: string; version: number; kind: string; status: string; description: string;
    subjectType: string; source: string; autoStart: boolean; anchorTypeFilter: string | null; anchorTypeField: string | null;
    notesProvenance: string | null; derivationReport: string | null;
  } | null;
  versions: TemplateVersion[];
  segments: SegmentView[]; endings: EndingView[]; validation: ValidationResult | null;
}
export interface BlockListItem { code: string; name: string; description: string; params: unknown; activeVersion: number; stepCount: number; }

export interface WfPatch { op: string; [k: string]: unknown }
export interface PatchResult { ok: boolean; applied: number; rejected: number; errors: { index: number; message: string }[]; validation: ValidationResult; }

// ---- action catalog (the served grammar — the palette) --------------------
// `label`/`explain` are the maker's language (№16.1.4 A); `reserved` (a string on
// the wire) is coerced truthy → the picker HIDES it. `nativeOps` is the curated
// system-verb set — the raw `op` free-text is retired.
export interface ActionSchema { key: string; name?: string; label: string; explain: string; verb: string; reserved?: string | boolean; paramSchema?: Record<string, string>; }
export interface NativeOpParam { key: string; label: string; input: "attribute" | "value" | "dateField" | "date" | "status"; }
export interface NativeOp { op: string; label: string; explain: string; params: NativeOpParam[]; }
export interface ActionCatalog {
  actions: ActionSchema[];
  nativeOps: NativeOp[];
  bindingSchema: Record<string, string>;
  effectVerbs: Record<string, Record<string, string>>;
  conditionSources: string[]; conditionOperators: string[];
  patchOps: { op: string; fields: Record<string, string>; notes: string }[];
  howToAuthor: string;
}

// ---- reads -----------------------------------------------------------------

// `canAuthor` (№16.1.1) rides on every authoring READ payload — the worker's
// canUseArea(workflow.author) check. Drives read-only vs editable + the teaching
// affordance so a non-author never sees a blank void.
export function useWorkflowTemplates(kind?: string) {
  return useQuery({
    queryKey: ["workflow-template", "list", kind ?? "all"],
    staleTime: 10_000,
    queryFn: () => read<{ templates: TemplateListItem[]; canAuthor: boolean }>("workflow_list_templates", kind ? { kind } : {}),
  });
}

export function useTemplateDetail(code: string | undefined, version?: number) {
  return useQuery({
    queryKey: ["workflow-template", "detail", code, version ?? "head"],
    enabled: !!code,
    staleTime: 5_000,
    queryFn: () => read<TemplateDetail & { canAuthor: boolean }>("workflow_template_detail", version ? { code, version } : { code }),
  });
}

export function useWorkflowBlocks() {
  return useQuery({
    queryKey: ["workflow-template", "blocks"],
    staleTime: 30_000,
    queryFn: () => read<{ blocks: BlockListItem[] }>("workflow_list_blocks", {}),
  });
}

export function useActionCatalog() {
  return useQuery({
    queryKey: ["workflow-template", "catalog"],
    staleTime: Infinity,
    queryFn: () => read<ActionCatalog>("workflow_action_catalog", {}),
  });
}

// ---- writes (all invalidate the template's detail + list) ------------------

function useInvalidate() {
  const qc = useQueryClient();
  return (code?: string) => {
    void qc.invalidateQueries({ queryKey: ["workflow-template", "list"] });
    void qc.invalidateQueries({ queryKey: ["workflow-template", "detail", code] });
    void qc.invalidateQueries({ queryKey: ["workflow-template", "blocks"] });
  };
}

/** The ONE authoring door — a batch of canvas/MCP patches → apply-then-validate. */
export function usePatchTemplate() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (v: { code: string; version: number; patches: WfPatch[] }) =>
      read<PatchResult>("workflow_patch_template", { code: v.code, version: v.version, patches: v.patches }),
    onSuccess: (_r, v) => invalidate(v.code),
  });
}

export function useCreateTemplate() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (v: { code: string; description?: string; subject_type?: string; kind?: string }) =>
      read<{ ok: boolean; code: string; version: number }>("workflow_create_template", v),
    onSuccess: (_r, v) => invalidate(v.code),
  });
}

export function usePublishTemplate() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (v: { code: string; version: number }) => read<{ ok: boolean; code: string; active_version: number }>("workflow_publish_template", v),
    onSuccess: (_r, v) => invalidate(v.code),
  });
}

export function useNewDraftVersion() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (v: { code: string; from_version?: number }) => read<{ ok: boolean; code: string; version: number }>("workflow_new_draft_version", v),
    onSuccess: (_r, v) => invalidate(v.code),
  });
}

export function useDiscardDraft() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (v: { code: string; version: number }) => read<{ ok: boolean; code: string; active_version: number }>("workflow_discard_draft", v),
    onSuccess: (_r, v) => invalidate(v.code),
  });
}
